import {Label} from 'platform/components';
import {Box, Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda-adjunct';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useApiEnums} from '../../../hooks/useApiEnums';
import {calculatePriceExclVat} from '../utils/calculatePriceExclVat';
import {formatVatRate} from '../utils/formatVatRate';

interface AuctionPricingGridProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
}

export function AuctionPricingGrid(props: AuctionPricingGridProps) {
  const formatCurrency = useFormatCurrency();
  const auction = props.auction;
  const auctionVehicle = auction.auctionVehicle;
  const {getEnum} = useApiEnums();
  const countryName =
    getEnum('country').find(({key}) => key === auctionVehicle.sellerCountry)?.label ??
    auctionVehicle.sellerCountry ??
    '';
  const priceExclVat = calculatePriceExclVat(
    auctionVehicle.originalPriceActual,
    auctionVehicle.vatRate
  );

  const formattedConvertedPrice = isNotNil(auctionVehicle.priceActual)
    ? (formatCurrency(auctionVehicle.priceActual, auctionVehicle.currency ?? '') ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const formattedOriginalPrice = isNotNil(auctionVehicle.originalPriceActual)
    ? (formatCurrency(auctionVehicle.originalPriceActual, auctionVehicle.originalCurrency ?? '') ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const formattedPriceExclVat = isNotNil(priceExclVat)
    ? (formatCurrency(priceExclVat, auctionVehicle.originalCurrency ?? '') ?? EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const pricingGridItems = {
    convertedPrice: {
      label: i18n.t('general.labels.priceWithVat'),
      value: formattedConvertedPrice,
    },
    carId: {
      label: i18n.t('entity.auction.labels.carId'),
      value: auctionVehicle.pk ?? EMPTY_PLACEHOLDER,
    },
    originalPrice: {
      label: i18n.t('entity.auction.labels.origCurrencyWithVat'),
      value: formattedOriginalPrice,
    },
    auctionId: {
      label: i18n.t('entity.auction.labels.auctionIdLabel'),
      value: auctionVehicle.auctionId ?? EMPTY_PLACEHOLDER,
    },
    priceExclVat: {
      label: i18n.t('entity.auction.labels.origCurrencyWithoutVat'),
      value: formattedPriceExclVat,
    },
    vin: {
      label: i18n.t('entity.vehicle.labels.vin'),
      value: auctionVehicle.vin ?? EMPTY_PLACEHOLDER,
    },
    vatDeductible: {
      label: i18n.t('general.labels.vatDeductible'),
      value:
        auctionVehicle.vatReclaimable === true
          ? i18n.t('general.labels.yes')
          : i18n.t('general.labels.no'),
    },
  } as const;

  return (
    <VStack spacing={2}>
      <Grid columns={[1, 2]} verticalSpacing={0}>
        {Object.entries(pricingGridItems).map(([testId, item]) => (
          <Box
            key={testId}
            paddingVertical={1}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            <HStack justify="space-between" align="center">
              <Label>{item.label}</Label>
              <Text size="small" color="primary" data-testid={suffixTestId(testId, props)}>
                {item.value}
              </Text>
            </HStack>
          </Box>
        ))}
      </Grid>
      <Show when={isNotNil(auctionVehicle.vatRate)}>
        <Text size="xSmall" color="tertiary" data-testid={suffixTestId('vatFooter', props)}>
          {i18n.t('entity.auction.labels.inclVat', {
            vatRate: formatVatRate(auctionVehicle.vatRate ?? 0),
            country: countryName,
          })}
        </Text>
      </Show>
    </VStack>
  );
}
